/** Default characters per second for typewriter effect. */
const DEFAULT_CPS = 35;

export interface TypewriterOptions {
  /** Characters per second. Defaults to 35. */
  cps?: number;
}

/**
 * Typewriter effect: reveals text character-by-character into a target element.
 * Returns a controller to complete or cancel the animation.
 */
export interface TypewriterController {
  /** Resolves when the full text is displayed (either naturally or via complete()). */
  done: Promise<void>;
  /** True while text is still being revealed. */
  isRevealing(): boolean;
  /** Immediately show all remaining text. */
  complete(): void;
  /** Cancel the animation entirely (e.g. on scene change). */
  cancel(): void;
}

export function typewrite(
  element: HTMLElement,
  text: string,
  options?: TypewriterOptions,
): TypewriterController {
  const cps = options?.cps ?? DEFAULT_CPS;
  const interval = 1000 / cps;

  let charIndex = 0;
  let cancelled = false;
  let revealing = true;
  let resolvePromise: (() => void) | null = null;

  const done = new Promise<void>((resolve) => {
    resolvePromise = resolve;
  });

  element.textContent = '';

  const timer = setInterval(() => {
    if (cancelled) {
      clearInterval(timer);
      return;
    }
    if (charIndex < text.length) {
      charIndex++;
      element.textContent = text.slice(0, charIndex);
    }
    if (charIndex >= text.length) {
      clearInterval(timer);
      revealing = false;
      resolvePromise?.();
    }
  }, interval);

  return {
    done,
    isRevealing() {
      return revealing && !cancelled;
    },
    complete() {
      if (!revealing || cancelled) return;
      clearInterval(timer);
      element.textContent = text;
      revealing = false;
      resolvePromise?.();
    },
    cancel() {
      if (cancelled) return;
      cancelled = true;
      revealing = false;
      clearInterval(timer);
      resolvePromise?.();
    },
  };
}
